import { DateTime } from 'luxon';
import { columnType, dataLayout, dtFormat, limitData } from '@/constants';

/**
 * 読取情報Selectタグ項目リスト作成
 * @param {!object[]} reads 読取情報配列
 * @returns {!object[]} 取込情報Selectタグ項目配列
 */
export function createReadSelectItems(reads) {
  // 未選択時のラベルをセット
  let items = [{ value: null, text: '読取ルールを選択します' }];

  // 読取情報からラベルをセット
  return items.concat(
    reads.map(function (x) {
      return { value: x.readId, text: x.readNm };
    })
  );
}

/**
 * 取込情報Selectタグ項目リスト作成
 * @param {!object[]} inputs 取込情報配列
 * @returns {!object[]} 取込情報Selectタグ項目配列
 */
export function createInputSelectItems(inputs) {
  // 未選択時のラベルをセット
  let items = [{ value: null, text: '取込ルールを選択します' }];

  // 取込情報からラベルをセット
  return items.concat(
    inputs.map(function (x) {
      return { value: x.inputId, text: x.inputNm };
    })
  );
}

/**
 * 照合ヘッダ項目リスト
 * @param {!object} compare 照合情報
 * @returns {!object[]} 照合ヘッダ項目配列(key,label)
 */
export function getCompareHeaderFields(compare) {
  const headerFields = compare.details
    .filter((cdetail) => cdetail.dataLayout === dataLayout.HEADER)
    .map((cdetail) => {
      return { key: `headerFormatValueNo${cdetail.compareNo}`, label: cdetail.columnNm };
    })
    .slice(0, limitData.ONE_HEADERS);
  if (headerFields.length === 0) {
    headerFields.push({ key: 'headerFormatValueNo1', label: '日付' });
    headerFields.push({ key: 'headerFormatValueNo2', label: '時刻' });
  }
  return headerFields;
}

/**
 * 照合明細項目リスト取得
 * @param {!object} compare 照合情報
 * @returns {!object[]} 照合明細項目配列(key,label)
 */
export function getCompareDetailFields(compare) {
  return compare.details
    .filter((cdetail) => cdetail.dataLayout === dataLayout.DETAIL)
    .map((cdetail) => {
      return { key: `detailFormatValueNo${cdetail.compareNo}`, label: cdetail.columnNm };
    })
    .slice(0, limitData.ONE_DETAILS);
}

/**
 * 照合先項目リスト取得
 * @param {!object} compare 照合情報
 * @returns {!object[]} 照合明細項目配列(key,label)
 */
export function getCompareDetailToFields(compare) {
  return compare.details
    .filter((cdetail) => cdetail.dataLayout === dataLayout.DETAIL)
    .map((cdetail) => {
      return { key: `formatToCompareNo${cdetail.compareNo}`, label: cdetail.columnNm };
    })
    .slice(0, limitData.ONE_DETAILS);
}

/**
 * 照合ヘッダ項目一覧リスト取得
 * @param {number} compareId 照合ID
 * @param {!object} compare 照合情報
 * @returns {!object[]} 照合ヘッダ項目配列(key,label)
 */
export function getCompareHeaderFieldsList(compare) {
  // 一覧表示項目選択
  const headerFields = getCompareHeaderFields(compare);
  const detailFields = getCompareDetailFields(compare);
  if (headerFields.length === 0) {
    headerFields.push({ key: 'headerFormatValueNo1', label: '日付' });
    headerFields.push({ key: 'headerFormatValueNo2', label: '時刻' });
  }
  return headerFields;
}

/**
 * 画面表示値フォーマット変換
 * @param {!object} compare 照合情報
 * @param {!object} item 照合画面情報
 */
export function convertItemFormat(compare, item) {
  // ヘッダ項目をフォーマットする
  const headerFields = compare.details
    .filter((cdetail) => cdetail.dataLayout === dataLayout.HEADER)
    .slice(0, limitData.ONE_HEADERS);
  if (headerFields.length === 0) {
    headerFields.push({ dataLayout: dataLayout.HEADER, compareNo: 1, columnType: columnType.TEXT });
    headerFields.push({ dataLayout: dataLayout.HEADER, compareNo: 2, columnType: columnType.TEXT });
  }
  for (let cdetail of headerFields) {
    const columnKey = `headerValueNo${cdetail.compareNo}`;
    if (columnKey in item) {
      convertValueFormat(item, cdetail, compare.dataLayout);
    }
  }

  // 明細項目をフォーマットする(1:1照合のヘッダ表示を考慮)
  const detailFields = compare.details
    .filter((cdetail) => cdetail.dataLayout === dataLayout.DETAIL)
    .slice(0, limitData.ONE_DETAILS);
  for (let cdetail of detailFields) {
    const valueKey = `detailValueNo${cdetail.compareNo}`;
    const toCompareKey = `toCompareNo${cdetail.compareNo}`;

    // ヘッダ表示をフォーマット
    if (valueKey in item) {
      convertValueFormat(item, cdetail);
    }

    // 明細表示をフォーマット
    if ('details' in item) {
      for (let i = 0; i < item.details.length; i++) {
        if (valueKey in item.details[i]) {
          convertValueFormat(item.details[i], cdetail);
        }
        if (toCompareKey in item.details[i]) {
          convertToCompareFormat(item.details[i], cdetail);
        }
      }
    }
  }
}

/**
 * 値フォーマット変換
 * @param {!object} value 値オブジェクト
 * @param {!object} cdetail 照合明細情報
 */
export function convertValueFormat(value, cdetail) {
  const layoutKey = cdetail.dataLayout === dataLayout.HEADER ? 'header' : 'detail';
  const columnKey = `${layoutKey}ValueNo${cdetail.compareNo}`;
  const formatKey = `${layoutKey}FormatValueNo${cdetail.compareNo}`;

  switch (cdetail.columnType) {
    case columnType.TEXT: // 文字列
      value[formatKey] = value[columnKey];
      break;
    case columnType.NUMBER: // 数値
      value[formatKey] = value[columnKey];
      break;
    case columnType.DATE: // 日付
      value[formatKey] = replaceDateValue(value[columnKey], cdetail.dateFormat);
      break;
    default:
      value[formatKey] = value[columnKey];
      break;
  }
}

/**
 * 比較先フォーマット変換
 * @param {!object} value 値オブジェクト
 * @param {!object} cdetail 照合明細情報
 */
export function convertToCompareFormat(value, cdetail) {
  const columnKey = `toCompareNo${cdetail.compareNo}`;
  const formatKey = `formatToCompareNo${cdetail.compareNo}`;

  switch (cdetail.columnType) {
    case columnType.TEXT: // 文字列
      value[formatKey] = value[columnKey];
      break;
    case columnType.NUMBER: // 数値
      value[formatKey] = value[columnKey];
      break;
    case columnType.DATE: // 日付
      value[formatKey] = replaceDateValue(value[columnKey], cdetail.dateFormat);
      break;
    default:
      value[formatKey] = value[columnKey];
      break;
  }
}

/**
 * 変換値を反映する
 * @param {?string} textValue テキスト値
 * @param {?number} convertId 変換ID
 * @param {!object[]} converts 変換情報配列
 * @returns {?string} 変換値設定後の値
 */
export function replaceConvertValue(textValue, convertId, converts) {
  // 後の空白を除去する
  textValue = textValue ? textValue.trimEnd() : null;
  textValue = textValue ? textValue : null;

  // 変換ID未指定の場合は値をそのまま返す
  if (!convertId) return textValue;

  // 値が空の場合はそのまま返す
  if (!textValue) return textValue;

  const targets = converts.filter(function (x) {
    return x.convertId === convertId;
  });

  // 優先順位順に変換前値を変換後値で置き換える
  let convertValue = textValue;
  for (const target of targets) {
    convertValue = convertValue.split(target['convertBefValue']).join(target['convertAftValue']);
  }

  return convertValue;
}

/**
 * 日付書式を反映する
 * @param {?string} textValue テキスト値
 * @param {?string} dateFormat 日付書式
 * @returns {?string} 日付書式反映後の値、null:変換NG
 */
export function replaceDateValue(textValue, dateFormat) {
  // 日付フォーマットが未指定の場合はそのまま返す
  if (!dateFormat) return textValue;

  // 値が空の場合はそのまま返す
  if (!textValue) return textValue;

  // 日付書式指定された場合は書式値を取得する
  const dateValue = DateTime.fromFormat(textValue, dtFormat.STD);

  // 日付変換できない場合はエラーとする
  if (!dateValue.isValid) return null;

  const formatValue = dateValue.toFormat(dateFormat);

  return formatValue;
}

/**
 * 日付書式で日付を取得する
 * @param {?string} textValue テキスト値
 * @param {?string} dateFormat 解析書式
 * @returns {?string} 解析書式で取得した値(yyyy-MM-dd HH:mm:ss.SSS)、null:解析NG
 */
export function getDateValue(textValue, dateFormat) {
  // 値が空の場合はそのまま返す
  if (!textValue) return null;

  // 書式が空の場合はそのまま返す
  if (!dateFormat) return null;

  // 日付データを取得する
  const dateValue = DateTime.fromFormat(textValue, dateFormat);

  // 日付変換できない場合はエラーとする
  if (!dateValue.isValid) return null;

  // 標準フォーマット(DBフォーマット)で返す
  return dateValue.toFormat(dtFormat.STD);
}
